import {
  type SpApiConnectionsResponse,
  type SpApiConnectResponse,
  type SpApiDisconnectResponse,
  type SpApiRegion,
  spApiConnectionsResponseSchema,
  spApiConnectResponseSchema,
  spApiDisconnectResponseSchema,
} from "@adpilot/shared";
import { api } from "./api";

/** Load Seller Central grants without conflating them with Amazon Ads connections. */
export async function fetchSpApiConnections(): Promise<SpApiConnectionsResponse> {
  const response = await api
    .get("auth/amazon/sp-api/status")
    .json<{ success: true; data: unknown }>();

  return spApiConnectionsResponseSchema.parse(response.data);
}

/** Row actions supply a Shop; standalone actions retain the current-Shop default. */
export async function fetchSpApiConnectUrl(shopId?: string): Promise<SpApiConnectResponse> {
  const response = await api
    .get("auth/amazon/sp-api/connect", {
      headers: shopId ? { "X-Shop-Id": shopId } : undefined,
    })
    .json<{ success: true; data: unknown }>();

  return spApiConnectResponseSchema.parse(response.data);
}

/**
 * Onboarding authorizes before any Shop exists, so it names the seller from the
 * Amazon Ads grant the backend still has cached.
 */
export async function fetchSpApiAccountConnectUrl(target: {
  selectionState: string;
  merchantId: string;
  region: SpApiRegion;
}): Promise<SpApiConnectResponse> {
  const response = await api
    .get("auth/amazon/sp-api/connect", {
      searchParams: {
        selectionState: target.selectionState,
        merchantId: target.merchantId,
        region: target.region,
      },
    })
    .json<{ success: true; data: unknown }>();

  return spApiConnectResponseSchema.parse(response.data);
}

/** Start a replacement grant while preserving the existing connection identity. */
export async function reauthorizeSpApiConnection(
  connectionId: string,
): Promise<SpApiConnectResponse> {
  const response = await api
    .post(`auth/amazon/sp-api/connections/${connectionId}/reauthorize`)
    .json<{ success: true; data: unknown }>();

  return spApiConnectResponseSchema.parse(response.data);
}

/** Ask Amazon now whether the grant still works; refetch the connections to see the result. */
export async function checkSpApiConnection(connectionId: string): Promise<void> {
  await api.post(`auth/amazon/sp-api/connections/${connectionId}/check`);
}

/** Revoke AdPraxis's local use of a Seller Central grant. */
export async function disconnectSpApiConnection(
  connectionId: string,
): Promise<SpApiDisconnectResponse> {
  const response = await api
    .delete(`auth/amazon/sp-api/connections/${connectionId}`)
    .json<{ success: true; data: unknown }>();

  return spApiDisconnectResponseSchema.parse(response.data);
}
